import { type ReactNode, useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";

import { cn } from "./cn";

// A modal dialog. It traps Tab inside itself, closes on Escape, moves focus in when it opens and back to
// the control that opened it when it closes. `aria-modal` tells a screen reader the page behind is not
// available. The title is the accessible name.
//
// A click on the backdrop does not close it: a form in it would lose its fields.
// The class `dialog` stays on the panel as a hook (`itinerary/components/save.tsx` queries `.dialog [aria-invalid]`). A page that needs a
// wider dialog sets the custom property `--dialog-width` on it.

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// The page behind a dialog does not scroll. Two dialogs can be open at once (a confirm over a drawer): the lock ends with the last one.
let openDialogs = 0;
function lockScroll(): () => void {
  openDialogs += 1;
  document.body.dataset.scrollLocked = "";
  return () => {
    openDialogs -= 1;
    if (openDialogs === 0) delete document.body.dataset.scrollLocked;
  };
}

type DialogProps = Readonly<{
  title: string;
  onClose: () => void;
  children: ReactNode;
  description?: ReactNode;
  /** Focus the heading when the dialog opens, so a screen reader reads the title and the description first. */
  focusTitle?: boolean;
  /**
   * The `id` of the title. A part inside the dialog (the form of `CommandDialog`) names itself with `aria-labelledby` pointing at it. A title
   * often holds a person's name ("Offboard Priya Nair"): a name must never be copied into an `aria-label`, which error reports copy.
   */
  titleId?: string;
  /** Classes for the panel, for example `[--dialog-width:48rem]` for a wider one. */
  className?: string | undefined;
}>;

export function Dialog({
  title,
  onClose,
  children,
  description,
  focusTitle = false,
  titleId: titleIdProp,
  className,
}: DialogProps) {
  const ownTitleId = useId();
  const titleId = titleIdProp ?? ownTitleId;
  const descriptionId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => lockScroll(), []);

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const node = panel.current;
    const first = focusTitle
      ? node?.querySelector<HTMLElement>("h2")
      : (node?.querySelector<HTMLElement>("[data-autofocus]") ??
        node?.querySelector<HTMLElement>(FOCUSABLE));
    (first ?? node)?.focus();
    return () => opener?.focus();
    // `focusTitle` is fixed for the life of a dialog, so this still runs once: when the dialog opens.
  }, [focusTitle]);

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      event.stopPropagation();
      onCloseRef.current();
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = [...(panel.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])];
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (first === undefined || last === undefined) {
      event.preventDefault();
      return;
    }
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === panel.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  return createPortal(
    <div className="dialog-backdrop fixed inset-0 z-[var(--lf-z-modal)] grid place-items-center overflow-y-auto bg-foreground/50 p-4">
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={cn(
          "dialog grid max-h-[calc(100dvh-2rem)] w-full max-w-[var(--dialog-width,32.5rem)] gap-4 overflow-y-auto rounded-md border border-border bg-popover p-5 text-popover-foreground shadow-lg sm:p-6",
          className,
        )}
        onKeyDown={onKeyDown}
      >
        <h2
          id={titleId}
          tabIndex={focusTitle ? -1 : undefined}
          className="m-0 text-xl leading-7 font-semibold tracking-tight"
        >
          {title}
        </h2>
        {description ? (
          <p
            id={descriptionId}
            className="dialog__description m-0 text-sm leading-6 text-muted-foreground"
          >
            {description}
          </p>
        ) : null}
        {children}
      </div>
    </div>,
    document.body,
  );
}
